import {Button, IconButton, openDialog} from 'platform/components';
import {Heading, Hide, HStack, Show, Space} from 'platform/foundation';

import {useSelector} from 'react-redux';

import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {InspectionState, useGetParticipationQuery} from '@dms/api/core';
import {useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {ErrorPage, hasAclDocsWithPermission, usePermissions} from '@dms/shared';
import {DigitalCertificateLink} from '@dms/shared-aftersales';

import {composePath, Nullish, suffixTestId, TestIdProps, useNavigate} from 'shared/web';

import {selectAudit, selectAuditState, selectStructure} from '../../../store/carAudit/selectors';
import {selectUserSelectedLanguage} from '../../../store/user/selectors';
import {getNextPossibleInspectionState} from '../../../utils/getNextPossibleInspectionState';
import {useConditionContext} from '../hooks/useConditionContext';
import {getTranslation} from '../utils/getTranslation';
import {AutoFillTemplates} from './AutoFillTemplates';
import {ConditionStatus} from './ConditionStatus';
import {ExportPDF} from './ExportPDF';
import {FinishInspection} from './FinishInspection';
import {InRevision} from './InRevision';
import {InspectionActions} from './InspectionActions';
import {ReopenInspection} from './ReopenInspection';
import {ToFinishingForm} from './ToFinishingForm';
import {ToRevisionInspection} from './ToRevisionInspection';
import {UpdateCondition} from './UpdateCondition';

interface InspectionHeaderProps extends TestIdProps {
  returnBackLink?: () => string;
  inspectionId: string;
  vehicleId: string | Nullish;
  isSinglePage?: boolean;
}

export function InspectionHeader(props: InspectionHeaderProps) {
  const navigate = useNavigate();
  const audit = useSelector(selectAudit);
  const auditState = useSelector(selectAuditState);
  const structure = useSelector(selectStructure);
  const locale = useSelector(selectUserSelectedLanguage);

  const {isDisabledForUser} = useConditionContext();

  const {data: vehicle} = useGetVehicleQuery(
    {vehicleId: props.vehicleId ?? ''},
    {skip: isNilOrEmpty(props.vehicleId)}
  );
  const {data: vehicleParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      recordId: props.vehicleId ?? '',
    },
    {skip: isNilOrEmpty(props.vehicleId)}
  );

  // State transitions are gated by the same ACL actions the BE checks for each transition
  const stateTransitionScope = {
    inspectionType: audit?.inspectionType,
    // Participation is not evaluated here, same as for updateInspection. The BE enforces it.
    participation: 'SKIP',
    inspectionState: audit?.state,
  } as const;

  const [
    hasCloseInspectionPermission,
    hasUpdateInspectionPermission,
    hasReadInspectionPermission,
    hasReopenInspectionPermission,
    hasBeginInspectionPermission,
    hasFinishInspectionPermission,
    hasReviseInspectionPermission,
    hasReturnForFinishingInspectionPermission,
  ] = usePermissions({
    permissionKeys: [
      'closeInspection',
      'updateInspection',
      'readInspectionDocument',
      'reopenInspection',
      'beginInspection',
      'finishInspection',
      'reviseInspection',
      'returnForFinishingInspection',
    ],
    scopes: {
      closeInspection: {
        inspectionType: audit?.inspectionType,
        // Gated by inspection type, not participation
        participation: 'SKIP',
      },
      updateInspection: stateTransitionScope,
      readInspectionDocument: {
        branchId: vehicle?.branchId,
        participation: vehicleParticipation,
      },
      reopenInspection: {
        inspectionType: audit?.inspectionType,
        // Gated by inspection type, not participation
        participation: 'SKIP',
      },
      beginInspection: stateTransitionScope,
      finishInspection: stateTransitionScope,
      reviseInspection: stateTransitionScope,
      returnForFinishingInspection: stateTransitionScope,
    },
  });

  if (!props.vehicleId) {
    return <ErrorPage message={i18n.t('page.vehicle.notifications.notFound')} />;
  }

  const nextPossibleInspectionStates = audit?.state
    ? getNextPossibleInspectionState(audit.state)
    : [];

  const canSwitchToInProgress = nextPossibleInspectionStates.includes(InspectionState.IN_PROGRESS);
  const canStartInspection =
    hasBeginInspectionPermission &&
    canSwitchToInProgress &&
    audit?.state === InspectionState.FOR_CHECK;
  // Refinement continues editing the inspection data, so it is gated by UPDATE scoped to the state
  const canStartRefinement =
    hasUpdateInspectionPermission &&
    canSwitchToInProgress &&
    audit?.state === InspectionState.TO_FINISHING;
  const canSendInspectionToRevision =
    hasFinishInspectionPermission &&
    nextPossibleInspectionStates.includes(InspectionState.TO_REVISION);
  const canSendInspectionToFinishing =
    hasReturnForFinishingInspectionPermission &&
    nextPossibleInspectionStates.includes(InspectionState.TO_FINISHING);
  const canCloseInspection =
    hasCloseInspectionPermission &&
    nextPossibleInspectionStates.includes(InspectionState.FINISHED_AUDIT);
  const canSendInspectionToInRevision =
    hasReviseInspectionPermission &&
    nextPossibleInspectionStates.includes(InspectionState.IN_REVISION);
  const canReopenInspection =
    hasUpdateInspectionPermission &&
    hasReopenInspectionPermission &&
    nextPossibleInspectionStates.includes(InspectionState.IN_PROGRESS) &&
    audit?.state === 'finished_audit';

  const returnToInspections = () => {
    navigate(composePath(vehiclesRoutes.conditionsInspections, {params: {id: props.vehicleId}}));
  };

  const handleBackButton = () => {
    if (props.isSinglePage) {
      return;
    }

    navigate(
      props.returnBackLink?.() ??
        composePath(vehiclesRoutes.conditionsInspections, {params: {id: props.vehicleId}})
    );
  };

  return (
    <>
      <HStack data-testid={suffixTestId('inspectionHeader', props)} justify="space-between">
        <HStack align="center" spacing={2}>
          <Hide when={props.isSinglePage}>
            <IconButton
              size="small"
              onClick={handleBackButton}
              icon="navigation/arrow_back_ios"
              data-testid={suffixTestId('inspectionHeader', props)}
            />
          </Hide>
          <Heading size={3} data-testid={suffixTestId('inspectionHeader-title', props)}>
            {structure ? getTranslation(locale, structure.name) : ''}
          </Heading>
          <ConditionStatus />
        </HStack>
        <HStack align="center" spacing={2}>
          <InspectionActions
            inspectionId={props.inspectionId}
            vehicleId={props.vehicleId}
            inspectionType={audit?.inspectionType}
          />

          <DigitalCertificateLink inspectionId={props.inspectionId} />

          <ExportPDF
            inspectionType={audit?.inspectionType}
            data-testid={suffixTestId('inspectionHeader', props)}
            canExport={hasAclDocsWithPermission(hasReadInspectionPermission)}
          />

          <Show when={isNotNilOrEmpty(props.vehicleId)}>
            <UpdateCondition
              vehicleId={props.vehicleId}
              data-testid={suffixTestId('inspectionHeader', props)}
            />
          </Show>

          <AutoFillTemplates
            data-testid={suffixTestId('inspectionHeader', props)}
            shouldUseAuditIdForUpdateTemplate={false}
          />
          {/* Sending to revision saves the form data first, so it also needs edit rights (UPDATE) */}
          <Show when={!isDisabledForUser && auditState && canSendInspectionToRevision}>
            <ToRevisionInspection
              onSuccess={props.isSinglePage ? undefined : returnToInspections}
            />
          </Show>
          <Show when={auditState && canSendInspectionToFinishing}>
            <Button
              variant="secondary"
              data-testid={suffixTestId('inspectionHeader', props)}
              onClick={() =>
                openDialog(
                  <ToFinishingForm
                    onSuccess={props.isSinglePage ? undefined : returnToInspections}
                  />,
                  {
                    title: i18n.t('general.actions.toFinishing'),
                  }
                )
              }
              title={i18n.t('general.actions.toFinishing')}
            />
          </Show>
          <Show when={canSendInspectionToInRevision}>
            <InRevision data-testid={suffixTestId('inspectionHeader', props)} />
          </Show>
          <Show when={canCloseInspection}>
            <FinishInspection
              onSuccess={props.isSinglePage ? undefined : returnToInspections}
              data-testid={suffixTestId('inspectionHeader', props)}
            />
          </Show>
          <Show when={canStartInspection}>
            <ReopenInspection
              inspectionId={props.inspectionId}
              title={i18n.t('general.actions.startInspection')}
              data-testid={suffixTestId('inspectionHeader', props)}
            />
          </Show>
          <Show when={canStartRefinement}>
            <ReopenInspection
              inspectionId={props.inspectionId}
              title={i18n.t('general.actions.startRefinement')}
              data-testid={suffixTestId('inspectionHeader', props)}
            />
          </Show>
          <Show when={canReopenInspection}>
            <ReopenInspection
              inspectionId={props.inspectionId}
              data-testid={suffixTestId('inspectionHeader', props)}
            />
          </Show>
        </HStack>
      </HStack>
      <Space vertical={5} />
    </>
  );
}
